/**
 * Tijdlijn per melding en bouwpunt (besluit Tom 01-10): de spelregels in
 * gewone TypeScript, zonder React of Supabase.
 *
 *  - `leidGebeurtenissenAf(oud, nieuw)` spiegelt de databasetrigger
 *    `log_melding_gebeurtenissen` (migratie 20261003120000_melding_tijdlijn.sql):
 *    welke gebeurtenissen horen bij een verschil tussen twee versies van een
 *    melding. De tests rekenen dit na; de database schrijft de echte regels.
 *    Wijzig je de trigger, wijzig dan ook deze functie (en andersom).
 *  - `filterVoorRol` bepaalt wat de melder ziet (geen interne regels).
 *  - `GEBEURTENIS_SOORTEN` geeft label en toon per soort voor de UI.
 */
import type { Tables } from '@/types/db';

export type Gebeurtenis = Tables<'melding_gebeurtenissen'>;

export type GebeurtenisSoort =
  // melding
  | 'ingestuurd'
  | 'doorvraag_antwoord'
  | 'vraag_ai'
  | 'vraag_terug'
  | 'antwoord'
  | 'herinnering'
  | 'onvolledig'
  | 'aangevuld'
  | 'gepland'
  | 'in_bouw'
  | 'uit_bouw'
  | 'pr_gekoppeld'
  | 'opgelost'
  | 'afgewezen'
  | 'heropend'
  | 'teruggezet'
  | 'feedback'
  | 'uitleg'
  | 'beoordeling'
  // bouwpunt
  | 'aangemaakt'
  | 'status'
  | 'commentaar';

export type GebeurtenisToon = 'neutraal' | 'info' | 'warning' | 'brand' | 'success' | 'error';

export interface SoortDefinitie {
  /** Korte kop op de tijdlijn, bijv. "Opgelost". */
  label: string;
  toon: GebeurtenisToon;
}

export const GEBEURTENIS_SOORTEN: Record<GebeurtenisSoort, SoortDefinitie> = {
  ingestuurd: { label: 'Ingestuurd', toon: 'info' },
  doorvraag_antwoord: { label: 'Doorvraag beantwoord', toon: 'info' },
  vraag_ai: { label: 'AI vroeg om aanvulling', toon: 'warning' },
  vraag_terug: { label: 'Vraag terug', toon: 'warning' },
  antwoord: { label: 'Antwoord van de melder', toon: 'brand' },
  herinnering: { label: 'Herinnering gestuurd', toon: 'neutraal' },
  onvolledig: { label: 'Onvolledig — wacht op melder', toon: 'warning' },
  aangevuld: { label: 'Aangevuld — weer bij Tom', toon: 'info' },
  gepland: { label: 'Wordt opgepakt', toon: 'neutraal' },
  in_bouw: { label: 'In bouw', toon: 'brand' },
  uit_bouw: { label: 'Uit de bouw gehaald', toon: 'neutraal' },
  pr_gekoppeld: { label: 'PR gekoppeld', toon: 'brand' },
  opgelost: { label: 'Opgelost / live', toon: 'success' },
  afgewezen: { label: 'Niet opgepakt', toon: 'neutraal' },
  heropend: { label: 'Heropend door de melder', toon: 'error' },
  teruggezet: { label: 'Terug naar nieuw', toon: 'neutraal' },
  feedback: { label: 'Terugmelding van de melder', toon: 'brand' },
  uitleg: { label: 'Uitleg aangepast', toon: 'success' },
  beoordeling: { label: 'Reactie aan de melder', toon: 'neutraal' },
  aangemaakt: { label: 'Op de bouwlijst gezet', toon: 'info' },
  status: { label: 'Status gewijzigd', toon: 'neutraal' },
  commentaar: { label: 'Commentaar voor Claude', toon: 'warning' },
};

export const FEEDBACK_LABEL: Record<string, string> = {
  werkt: 'Werkt',
  werkt_niet: 'Werkt nog niet',
  begrepen: 'Begrepen',
  niet_begrepen: 'Niet begrepen',
};

/** Statuslabels voor bouwpunten op de tijdlijn ("gepland → gebouwd"). */
const BOUW_STATUS_LABEL: Record<string, string> = {
  gepland: 'Te bouwen',
  bezig: 'In aanbouw',
  gebouwd: 'Gebouwd',
  feedback: 'Feedback',
  akkoord: 'Akkoord',
  geparkeerd: 'Geparkeerd',
};

export function soortDefinitie(soort: string): SoortDefinitie {
  return GEBEURTENIS_SOORTEN[soort as GebeurtenisSoort] ?? { label: soort, toon: 'neutraal' };
}

/**
 * De kop van één regel. Voor een bouwpunt-statuswissel de nieuwe status,
 * voor feedback de gegeven waarde.
 */
export function gebeurtenisKop(g: Pick<Gebeurtenis, 'soort' | 'naar_status' | 'details'>): string {
  if (g.soort === 'status' && g.naar_status) {
    return BOUW_STATUS_LABEL[g.naar_status] ?? g.naar_status;
  }
  if (g.soort === 'feedback') {
    const waarde = leesDetails(g.details).feedback;
    const label = waarde ? FEEDBACK_LABEL[waarde] : undefined;
    return label ? `Terugmelding: ${label.toLowerCase()}` : soortDefinitie(g.soort).label;
  }
  return soortDefinitie(g.soort).label;
}

export interface GebeurtenisDetails {
  pr_url?: string;
  pr_nummers?: string;
  feedback?: string;
  foto_paths?: string[];
  bouw_status?: string;
  soort?: string;
  ernst?: string;
  scherm?: string;
  backfill?: boolean;
}

export function leesDetails(details: unknown): GebeurtenisDetails {
  return details && typeof details === 'object' && !Array.isArray(details)
    ? (details as GebeurtenisDetails)
    : {};
}

/** Wie de tijdlijn bekijkt: beheerder/meekijker zien ook de interne regels. */
export type TijdlijnRol = 'beheerder' | 'meekijker' | 'melder';

export function filterVoorRol<T extends Pick<Gebeurtenis, 'intern'>>(
  items: readonly T[],
  rol: TijdlijnRol,
): T[] {
  if (rol === 'melder') return items.filter((g) => !g.intern);
  return [...items];
}

/** Oplopend op tijd; bij gelijke tijd op volgnummer (meerdere regels per update). */
export function sorteerGebeurtenissen<T extends Pick<Gebeurtenis, 'created_at' | 'nr'>>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    if (ta !== tb) return ta - tb;
    return a.nr - b.nr;
  });
}

/** "zojuist", "5 min geleden", "3 uur geleden", "gisteren", "4 dagen geleden", anders de datum. */
export function relatieveTijd(iso: string, nu: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sec = Math.round((nu.getTime() - d.getTime()) / 1000);
  if (sec < 45) return 'zojuist';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min geleden`;
  const uur = Math.round(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  const dagen = Math.round(uur / 24);
  if (dagen === 1) return 'gisteren';
  if (dagen < 14) return `${dagen} dagen geleden`;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

export function absoluteTijd(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Afleiding uit een verschil — spiegel van de trigger log_melding_gebeurtenissen
// ---------------------------------------------------------------------------

/** De kolommen die de trigger leest. */
export interface MeldingSnapshot {
  status: string;
  bouw_status?: string | null;
  pr_url?: string | null;
  uitleg?: string | null;
  beoordeling?: string | null;
  feedback?: string | null;
  feedback_opmerking?: string | null;
  feedback_op?: string | null;
  doorvraag?: unknown;
}

export interface AfgeleideGebeurtenis {
  soort: GebeurtenisSoort;
  van_status?: string | null;
  naar_status?: string | null;
  tekst?: string | null;
  intern: boolean;
  details?: GebeurtenisDetails;
}

interface DoorvraagVorm {
  vragen?: { vraag?: string; antwoord?: string }[];
  vragen_terug?: { soort?: string; tekst?: string }[];
}

function doorvraag(d: unknown): Required<DoorvraagVorm> {
  const v = d && typeof d === 'object' ? (d as DoorvraagVorm) : {};
  return {
    vragen: Array.isArray(v.vragen) ? v.vragen : [],
    vragen_terug: Array.isArray(v.vragen_terug) ? v.vragen_terug : [],
  };
}

const leeg = (s: string | null | undefined) => !s || s.trim() === '';
const kort = (s: string) => s.trim().slice(0, 200);

const VT_SOORT: Record<string, GebeurtenisSoort> = {
  ai: 'vraag_ai',
  tom: 'vraag_terug',
  melder: 'antwoord',
  herinnering: 'herinnering',
};

/**
 * Welke gebeurtenissen horen bij de overgang `oud` → `nieuw`. `oud = null`
 * is het insturen. Volgorde = volgorde waarin de trigger ze schrijft.
 *
 * `viaFeedbackRpc`: de feedback-RPC plakt zelf "Heropend door melder: …"
 * achter de beoordeling; die wijziging is al de feedback-regel.
 */
export function leidGebeurtenissenAf(
  oud: MeldingSnapshot | null,
  nieuw: MeldingSnapshot,
  opties: { viaFeedbackRpc?: boolean } = {},
): AfgeleideGebeurtenis[] {
  const uit: AfgeleideGebeurtenis[] = [];
  const dvOud = doorvraag(oud?.doorvraag);
  const dvNieuw = doorvraag(nieuw.doorvraag);

  // a) Ingestuurd
  if (!oud) uit.push({ soort: 'ingestuurd', naar_status: nieuw.status, intern: false });

  // b) Feedback: élke ronde
  if (
    oud &&
    nieuw.feedback &&
    (nieuw.feedback !== (oud.feedback ?? null) ||
      (nieuw.feedback_op ?? null) !== (oud.feedback_op ?? null) ||
      (nieuw.feedback_opmerking ?? null) !== (oud.feedback_opmerking ?? null))
  ) {
    uit.push({
      soort: 'feedback',
      van_status: oud.status,
      tekst: leeg(nieuw.feedback_opmerking) ? null : nieuw.feedback_opmerking!.trim(),
      intern: false,
      details: { feedback: nieuw.feedback },
    });
  }

  // c) Nieuw beantwoorde AI-vragen
  dvNieuw.vragen.forEach((v, i) => {
    const o = dvOud.vragen[i];
    if (!leeg(v.antwoord) && (!o || o.antwoord !== v.antwoord)) {
      uit.push({
        soort: 'doorvraag_antwoord',
        tekst: `${v.vraag ?? ''}\n${v.antwoord}`,
        intern: false,
      });
    }
  });

  // d) Nieuwe items in vragen_terug
  let vtGegroeid = false;
  for (let i = dvOud.vragen_terug.length; i < dvNieuw.vragen_terug.length; i++) {
    const item = dvNieuw.vragen_terug[i];
    vtGegroeid = true;
    uit.push({
      soort: VT_SOORT[item.soort ?? ''] ?? 'vraag_terug',
      tekst: item.tekst ?? null,
      intern: false,
    });
  }

  if (!oud) return uit;

  // e) Statuswijziging
  if (nieuw.status !== oud.status) {
    let soort: GebeurtenisSoort;
    switch (nieuw.status) {
      case 'opgelost':
      case 'afgewezen':
      case 'gepland':
      case 'onvolledig':
        soort = nieuw.status;
        break;
      case 'nieuw':
        if (nieuw.feedback === 'werkt_niet' && nieuw.feedback !== (oud.feedback ?? null)) {
          soort = 'heropend';
        } else if (oud.status === 'onvolledig') {
          soort = 'aangevuld';
        } else {
          soort = 'teruggezet';
        }
        break;
      default:
        soort = 'status';
    }
    if (!(soort === 'onvolledig' && vtGegroeid)) {
      uit.push({
        soort,
        van_status: oud.status,
        naar_status: nieuw.status,
        intern: false,
        details: {
          ...(nieuw.bouw_status ? { bouw_status: nieuw.bouw_status } : {}),
          ...(nieuw.pr_url ? { pr_url: nieuw.pr_url } : {}),
        },
      });
    }
  }

  // f) In bouw / uit bouw / PR
  if ((nieuw.bouw_status ?? null) !== (oud.bouw_status ?? null)) {
    if (nieuw.bouw_status === 'in_bouw') {
      uit.push({
        soort: 'in_bouw',
        van_status: oud.status,
        naar_status: nieuw.status,
        intern: false,
        details: nieuw.pr_url ? { pr_url: nieuw.pr_url } : {},
      });
    } else if (nieuw.status === oud.status) {
      uit.push({
        soort: 'uit_bouw',
        van_status: oud.status,
        naar_status: nieuw.status,
        intern: false,
      });
    }
  } else if ((nieuw.pr_url ?? null) !== (oud.pr_url ?? null) && !leeg(nieuw.pr_url)) {
    uit.push({ soort: 'pr_gekoppeld', intern: false, details: { pr_url: nieuw.pr_url! } });
  }

  // g) Uitleg
  if ((nieuw.uitleg ?? null) !== (oud.uitleg ?? null) && !leeg(nieuw.uitleg)) {
    uit.push({ soort: 'uitleg', tekst: kort(nieuw.uitleg!), intern: false });
  }

  // h) Beoordeling (intern)
  if (
    !opties.viaFeedbackRpc &&
    (nieuw.beoordeling ?? null) !== (oud.beoordeling ?? null) &&
    !leeg(nieuw.beoordeling)
  ) {
    uit.push({ soort: 'beoordeling', tekst: kort(nieuw.beoordeling!), intern: true });
  }

  return uit;
}
